/**
 * Is a worker-maintained snapshot as fresh as it should be RIGHT NOW?
 *
 * The breadth worker rewrites `scanner_topdown_snapshot` /
 * `scanner_breadth_snapshot` every ~60s during RTH and then idles. So "how old
 * is this payload" is not on its own an answer: 14 hours old at 08:00 ET is
 * exactly right, and 6 minutes old at 11:00 ET means nothing has run.
 *
 * This exists because a stalled worker is silent. The tables simply stop
 * advancing — the panes keep serving the last values they computed rather than
 * going blank, and the freshness clause in `SessionStatus` said "Market Closed
 * · 3h ago", which is time since the market's LAST CLOSE, a fact about the
 * calendar rather than about the data. A local checkout ran two days behind
 * without a single surface saying so.
 *
 * Two regimes, because the expected cadence has two:
 *
 *   inside RTH (past the opening grace)
 *       A healthy worker has written within the last minute. Stale once the
 *       payload is older than RTH_MAX_SNAPSHOT_AGE_MS.
 *
 *   everything else — pre-market, post-close, overnight, weekends, holidays,
 *   and the first few minutes of a session
 *       Age is the wrong measure; the worker is idle by design. The question
 *       is whether the payload covers the most recent COMPLETED session, so we
 *       compare it against that session's close. A snapshot written at 16:05
 *       stays fresh all night and all weekend; one written before the last
 *       close is a session behind however recently it was touched.
 *
 * The opening grace is load-bearing. At 09:30:01 a perfectly healthy worker's
 * newest write is still yesterday's post-close roll, so the RTH rule would fire
 * every trading morning until the first tick landed. A warning that is wrong
 * daily gets ignored, which is the failure this module exists to prevent — so
 * inside the grace we fall back to the session rule, which still catches a
 * genuinely dead worker at the open (its payload predates yesterday's close).
 *
 * The closing grace is the same idea at the other bell. The worker's last RTH
 * tick is stamped ~15:59, then it spends ~2 minutes on the one-shot post-close
 * roll (`TimeoutStopSec=180`). A live tab still holding that 15:59 payload is
 * one minute shy of today's close, not "a full day behind", and the client
 * has also just stopped polling (`useFetch` is RTH-gated). Without a grace
 * the chip lights at 16:00:01 every evening — and a refresh "fixes" it only
 * because that remount is what finally downloads the roll. A payload that
 * also predates today's open is still stale immediately.
 */
import { msSinceLastClose, msSinceRthOpen, todayRegularOpenMs } from "@/lib/marketHours";

/** How old a payload may get during RTH before it counts as stale.
 *
 *  Five worker cycles. Tight enough that a dead worker shows up inside the
 *  first few minutes, loose enough to ride out one slow tick (a full 11-universe
 *  pass runs 60-80s) plus the client's own 30-60s refetch, neither of which is
 *  a fault. */
export const RTH_MAX_SNAPSHOT_AGE_MS = 5 * 60_000;

/**
 * How long after the cash close a same-session RTH snapshot may still sit
 * in a live tab before `missed-session` fires.
 *
 * The breadth worker's post-close roll is one-shot and measured ~115 s
 * (deploy `TimeoutStopSec=180`). Ten minutes covers a slow roll plus the
 * client's own refetch without hiding a worker that never wrote Friday.
 */
export const CLOSE_ROLL_GRACE_MS = 10 * 60_000;

/**
 * How long the scanner tolerates a frozen snapshot before recomputing breadth
 * ITSELF — a different question from the one above, with a different cost.
 *
 * These were the same constant until 2026-09-03, and that cost hours of CPU a
 * day. The two decisions are not alike:
 *
 *   "tell the user this is stale"  — a false positive is an alarmist pill.
 *   "take over the breadth worker" — a false positive is 130-280s of duplicate
 *                                    full-`scanner_bars` scans, on the same
 *                                    database and the same box as the worker it
 *                                    is duplicating.
 *
 * The second one is self-reinforcing, which is what made it expensive: the
 * duplicate work slows the breadth worker's NEXT cycle, which makes the next
 * false positive more likely. Measured on 2026-09-03 the scanner took over 43
 * times against 96 correct skips — 31% of its cycles — while the breadth worker
 * was healthy the entire session.
 *
 * The reason it fired was an assumption baked into the 5-minute value: that
 * "a full 11-universe pass runs 60-80s". That session's passes ran 136s to
 * 387s (median ~250s), so a HEALTHY worker's payload routinely aged past five
 * minutes between writes.
 *
 * This value is deliberately NOT a cycle-time estimate — those rot, which is
 * exactly what happened. It is the answer to "how long may breadth output sit
 * frozen before somebody else must rebuild it", and 15 minutes carries ~2.3x
 * headroom over the worst pass observed. Liveness is judged separately, and
 * faster, by `breadth_worker_alive` (pinged every 30s AND from inside the
 * compute), so a worker that has actually STOPPED is still caught in ~90s —
 * quicker than the old rule managed.
 *
 * Override with `BREADTH_TAKEOVER_AGE_MS` for a box where the pass is slower
 * still; raising it delays recovery from a crash-loop, nothing else.
 */
export const BREADTH_TAKEOVER_AGE_MS = Math.max(
  RTH_MAX_SNAPSHOT_AGE_MS,
  Number(process.env.BREADTH_TAKEOVER_AGE_MS ?? "") || 15 * 60_000,
);

/** Why the scanner is (or is not) about to rebuild breadth itself. */
export type BreadthTakeoverReason =
  /** The breadth worker is pinging and completing passes — leave it alone. */
  | "worker-healthy"
  /** Off-RTH, and the snapshot already covers the last session close — do
   *  not rebuild every idle hour. */
  | "off-rth-current"
  /** Off-RTH, and the snapshot predates the last session close. One roll. */
  | "off-rth"
  /** No liveness ping for 90s: three missed 30s pings. Stopped, not slow. */
  | "worker-silent"
  /** Pinging, but no COMPLETED pass in BREADTH_TAKEOVER_AGE_MS — a crash-loop
   *  restarts often enough to keep pinging while never finishing. */
  | "snapshot-frozen";

export type BreadthTakeover = {
  takeOver: boolean;
  reason: BreadthTakeoverReason;
  aliveAgeMs: number;
  snapAgeMs: number;
};

/**
 * Should the scanner recompute breadth ITSELF this cycle?
 *
 * Pure and exported so `npm run workercoord:check` can pin it against the real
 * numbers a live session produced. The repo has been here before: a guard that
 * keeps its own copy of a comparison agrees with every broken version of it
 * (see `lib/scanner/breadthLocalServe.ts`). So the scanner and the check call
 * exactly this function.
 *
 * Liveness and completion are deliberately SEPARATE tests against separate
 * clocks, because "slow" and "dead" are different states that the old single
 * 5-minute rule could not tell apart — and getting that wrong cost 43 duplicate
 * precomputes in one session.
 */
export function breadthTakeoverDecision(args: {
  now: number;
  /** `scanner_meta.breadth_worker_alive` — pinged every 30s AND mid-compute. */
  aliveAt: number;
  /** `MAX(scanner_topdown_snapshot.computed_at)` — advances only on a COMPLETED pass. */
  snapshotAt: number;
  inRth: boolean;
  /** How long the regular session has been open (ms); null outside it.
   *  Defaults to the calendar's answer for `now`; a check passes its own. */
  sinceOpenMs?: number | null;
}): BreadthTakeover {
  const aliveAgeMs = args.aliveAt > 0 ? args.now - args.aliveAt : Number.POSITIVE_INFINITY;
  const snapAgeMs = args.snapshotAt > 0 ? args.now - args.snapshotAt : Number.POSITIVE_INFINITY;
  const base = { aliveAgeMs, snapAgeMs };

  if (!args.inRth) {
    // Breadth skips its ticks off-RTH by design. The scanner used to take over
    // every idle hour and spend 2–3 minutes rebuilding a snapshot that already
    // covered Friday's close. Take over only when the payload predates the
    // last session close (or there is no payload at all).
    const sinceClose = msSinceLastClose(args.now);
    if (sinceClose == null) {
      return args.snapshotAt > 0
        ? { ...base, takeOver: false, reason: "off-rth-current" }
        : { ...base, takeOver: true, reason: "off-rth" };
    }
    const lastCloseAt = args.now - sinceClose;
    if (args.snapshotAt < lastCloseAt) {
      return { ...base, takeOver: true, reason: "off-rth" };
    }
    return { ...base, takeOver: false, reason: "off-rth-current" };
  }
  if (!(aliveAgeMs < BREADTH_WORKER_SILENT_MS)) {
    return { ...base, takeOver: true, reason: "worker-silent" };
  }
  // "No completed pass in fifteen minutes" has to be measured from when a pass
  // became DUE, not from the last one that finished. Breadth skips its ticks
  // outside the session by design, so at 09:31 its last completed pass is last
  // evening's: hours old, and a sign of nothing. Measured from that pass, the
  // scanner read a healthy worker as crash-looping at EVERY open and ran its
  // own full pass beside the worker's first one (2026-10-02, 09:31:57: "last
  // completed pass is 8319s old", then 259.5 s of duplicate work while the
  // worker's own first pass took 254 s; the same on 10-01). So the clock
  // starts at the later of the last completed pass and this session's open.
  // A database that has never been precomputed has nothing to wait for.
  const sinceOpenMs = args.sinceOpenMs === undefined ? msSinceRthOpen(args.now) : args.sinceOpenMs;
  const overdueMs = args.snapshotAt > 0 && sinceOpenMs != null ? Math.min(snapAgeMs, sinceOpenMs) : snapAgeMs;
  if (!(overdueMs < BREADTH_TAKEOVER_AGE_MS)) {
    return { ...base, takeOver: true, reason: "snapshot-frozen" };
  }
  return { ...base, takeOver: false, reason: "worker-healthy" };
}

/**
 * How long the breadth worker may go without a liveness ping before the
 * scanner calls it stopped and rebuilds breadth itself.
 *
 * It was 90 s (three missed 30 s pings) on the premise that
 * `breadth_worker_alive` is written from inside the compute, so a worker
 * mid-pass keeps it fresh. On production the premise does not hold: one step
 * of a 150 to 224 s pass runs 97 to 203 s between pings (a synchronous walk
 * or statement holds the loop, and the 30 s timer cannot fire inside it), and
 * the scanner took over four times on 2026-10-19 (11:06, 12:11, 14:16, 15:20)
 * and six times on 10-16, each inside a tick that then completed normally 1
 * to 2 minutes later: 130 to 280 s of duplicate breadth work on the busiest
 * unit, in the session, each time.
 *
 * The line between slow and stopped is the unit's own: `mpp-breadth` has
 * `WatchdogSec=300`, petted by the same ping, so a worker silent for 300 s is
 * being killed and restarted by systemd (RestartSec=5). The scanner therefore
 * waits a little past that: a worker silent for 330 s is one that is not
 * coming back on its own. What this costs: a worker that is dead AND not
 * restarting (its unit stopped by hand) is taken over after 5.5 minutes
 * instead of 1.5; a crash-loop is still caught by the completed-pass test
 * above. `npm run workercoord:check` holds the constant above the unit's
 * watchdog and pins the 10-19 take-overs as cases that must not take over.
 * `BREADTH_WORKER_SILENT_MS` in the scanner's environment overrides it
 * (90000 is the way back).
 */
export const BREADTH_WORKER_SILENT_MS = Math.max(
  30_000,
  Number(process.env.BREADTH_WORKER_SILENT_MS ?? "") || 330_000,
);

export type StaleReason =
  /** Inside RTH and nothing has rewritten the snapshot for minutes. */
  | "rth-idle"
  /** The payload predates the last completed session's close. */
  | "missed-session";

export type SnapshotFreshness = {
  stale: boolean;
  /** Age of the payload in ms — null when there is no `computedAt` to judge. */
  ageMs: number | null;
  /** Null when fresh. */
  reason: StaleReason | null;
};

const FRESH: SnapshotFreshness = { stale: false, ageMs: null, reason: null };

export function snapshotFreshness(
  computedAt: number | null | undefined,
  now: number = Date.now(),
): SnapshotFreshness {
  if (computedAt == null || !Number.isFinite(computedAt)) return FRESH;

  // Clamp rather than report a negative age: a payload stamped slightly in the
  // future is clock skew between this browser and the worker's host, not news.
  const ageMs = Math.max(0, now - computedAt);

  const sinceOpen = msSinceRthOpen(now);
  if (sinceOpen != null && sinceOpen > RTH_MAX_SNAPSHOT_AGE_MS) {
    return ageMs > RTH_MAX_SNAPSHOT_AGE_MS
      ? { stale: true, ageMs, reason: "rth-idle" }
      : { stale: false, ageMs, reason: null };
  }

  const sinceClose = msSinceLastClose(now);
  // No close found in the trailing ~10 days should not happen for US equities,
  // but claiming staleness on a calendar we could not resolve would be an
  // assertion we have not earned.
  if (sinceClose == null) return { stale: false, ageMs, reason: null };

  const lastCloseAt = now - sinceClose;
  if (computedAt >= lastCloseAt) return { stale: false, ageMs, reason: null };

  // Last RTH tick is stamped before the close by construction. During the
  // roll grace that is expected — only a payload from before today's open
  // is actually a session behind (yesterday's roll, or older).
  if (sinceClose < CLOSE_ROLL_GRACE_MS) {
    const todayOpenAt = todayRegularOpenMs(now);
    if (todayOpenAt == null) return { stale: false, ageMs, reason: null };
    return computedAt < todayOpenAt
      ? { stale: true, ageMs, reason: "missed-session" }
      : { stale: false, ageMs, reason: null };
  }

  return { stale: true, ageMs, reason: "missed-session" };
}

/** One line for a tooltip / banner, naming the likely cause and the fix. */
export function staleSnapshotHint(reason: StaleReason): string {
  return reason === "rth-idle"
    ? "The market is open but nothing has rewritten this snapshot for several minutes — the breadth worker is probably not running. Every column except the live-streamed ones is frozen at the time shown."
    : "This snapshot predates the last completed session, so it is missing at least one full day. The post-close roll did not land for this universe — start the breadth worker if it is not already running.";
}
